if (!customElements.get('ask-matty')) {
  customElements.define('ask-matty', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const details = this.querySelector('details');
      const trigger = this.querySelector('summary');
      const closeButton = this.querySelector('button');
      closeButton.hidden = false;
      // Supplied video frames replace the still briefly; layout stays stationary.
      const image = this.querySelector('.ask-matty__image');
      const artwork = this.querySelector('.ask-matty__artwork');
      const wave = this.querySelector('.ask-matty__wave');
      const waveSource = this.dataset.mattyWaveSrc;
      let waveReady = false;
      let waveFailed = false;
      let preloader;
      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      const welcomeKey = 'cmw-matty-welcome-v1';
      let greetingPending = !this.constructor.welcomeSeen;
      try {
        if (sessionStorage.getItem(welcomeKey) === '1') greetingPending = false;
      } catch (_) {}
      let motionTimer;
      let finishTimer;
      let pressing = false;
      const stopMotion = () => {
        clearTimeout(motionTimer);
        clearTimeout(finishTimer);
        artwork.classList.remove('matty-waving');
        image.classList.remove('matty-bounce');
      };
      const canAnimate = () => !document.hidden && !motionPreference.matches && this.isConnected;
      const prepareWave = () => {
        if (preloader || !waveSource || !canAnimate()) return;
        preloader = new Image();
        preloader.fetchPriority = 'low';
        preloader.addEventListener('load', () => {
          waveReady = true;
          wave.style.backgroundImage = `url("${waveSource}")`;
        }, options);
        preloader.addEventListener('error', () => {
          waveFailed = true;
          stopMotion(); // Keep the original still and working menu on asset failure.
        }, options);
        preloader.src = waveSource;
      };
      const scheduleMotion = () => {
        clearTimeout(motionTimer);
        if (!canAnimate() || (!this.hasAttribute('data-bundle') && details.open) || pressing || waveFailed || !waveSource || document.readyState === 'loading') return;
        prepareWave();
        motionTimer = setTimeout(() => {
          if (!canAnimate() || (!this.hasAttribute('data-bundle') && details.open) || pressing) return;
          // Also skip when an existing cart/modal visibility rule hides Matty.
          if (waveReady && this.getClientRects().length && getComputedStyle(this).visibility !== 'hidden') {
            if (greetingPending) {
              greetingPending = false;
              this.constructor.welcomeSeen = true;
              try { sessionStorage.setItem(welcomeKey, '1'); } catch (_) {}
            }
            artwork.classList.add('matty-waving');
            finishTimer = setTimeout(() => {
              artwork.classList.remove('matty-waving');
              scheduleMotion();
            }, 3000);
          } else scheduleMotion();
        }, greetingPending ? 3000 : 12000);
      };
      const syncMotion = () => { stopMotion(); scheduleMotion(); };
      const bounce = () => {
        stopMotion();
        if (!canAnimate()) return;
        image.classList.add('matty-bounce');
        finishTimer = setTimeout(() => {
          image.classList.remove('matty-bounce');
          scheduleMotion();
        }, 360);
      };
      details.addEventListener('toggle', () => {
        // Let the brief activation bounce finish; no idle motion while open.
        if (details.open && image.classList.contains('matty-bounce')) clearTimeout(motionTimer);
        else syncMotion();
      }, options);
      document.addEventListener('visibilitychange', syncMotion, options);
      motionPreference.addEventListener('change', syncMotion, options);
      // Do not let unrelated images or apps delay the greeting.
      document.addEventListener('DOMContentLoaded', scheduleMotion, { ...options, once: true });
      window.addEventListener('pageshow', syncMotion, options);
      window.addEventListener('pagehide', stopMotion, options);
      this.cleanupMotion = () => {
        stopMotion();
        preloader?.removeAttribute('src');
        wave.style.removeProperty('background-image');
      };
      scheduleMotion();
      if (this.hasAttribute('data-bundle')) {
        closeButton.hidden = true;
        this.addEventListener('matty-react', bounce, options);
        this.addEventListener('matty-celebrate', () => { greetingPending = true; syncMotion(); }, options);
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && window.matchMedia('(max-width: 749px)').matches) {
            details.open = false;
            trigger.focus();
          }
        }, options);
        return;
      }
      const close = (restoreFocus = false) => {
        details.open = false;
        syncMotion();
        if (restoreFocus) trigger.focus();
      };
      closeButton.addEventListener('click', () => close(true), options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && details.open) {
          event.preventDefault();
          event.stopPropagation();
          close(true);
        }
      }, options);
      document.addEventListener('click', (event) => {
        if (details.open && !this.contains(event.target)) close();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (details.open && event.relatedTarget && !this.contains(event.relatedTarget)) close();
      }, options);
      window.addEventListener('pagehide', () => { close(); stopMotion(); }, options);

      // Pointer handling is limited to the launcher; panel links retain normal behaviour.
      const storageKey = 'cmw-matty-position-v1';
      const margin = 8;
      const viewport = () => ({ width: document.documentElement.clientWidth, height: window.innerHeight });
      const clamp = (value, max) => Math.max(margin, Math.min(value, Math.max(margin, max)));
      const placeLabel = () => {
        if (!window.matchMedia('(max-width: 480px)').matches) {
          this.removeAttribute('data-matty-label-left');
          this.style.removeProperty('--matty-label-width');
          return;
        }
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const view = viewport();
        const right = Math.max(0, view.width - rect.right - margin - 10);
        const left = Math.max(0, rect.left - margin - 10);
        const onLeft = right < 160 && left > right;
        this.toggleAttribute('data-matty-label-left', onLeft);
        this.style.setProperty('--matty-label-width', `${Math.min(160, onLeft ? left : right)}px`);
      };
      const placePanel = () => {
        if (!this.hasAttribute('data-matty-moved')) return;
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const view = viewport();
        const above = Math.max(0, rect.top - margin - 12);
        const below = Math.max(0, view.height - rect.bottom - margin - 12);
        const panelWidth = Math.min(360, view.width - margin * 2);
        const panelLeft = clamp(rect.left, view.width - panelWidth - margin) - rect.left;
        this.style.setProperty('--matty-panel-left', `${panelLeft}px`);
        this.style.setProperty('--matty-panel-height', `${Math.max(above, below)}px`);
        this.toggleAttribute('data-matty-below', below > above);
      };
      const move = (x, y) => {
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const view = viewport();
        this.style.left = `${clamp(x, view.width - rect.width - margin)}px`;
        this.style.top = `${clamp(y, view.height - rect.height - margin)}px`;
        this.style.bottom = 'auto';
        this.setAttribute('data-matty-moved', '');
        placeLabel();
        placePanel();
      };
      const remember = () => {
        const rect = this.getBoundingClientRect();
        const view = viewport();
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            x: (rect.left - margin) / Math.max(1, view.width - rect.width - margin * 2),
            y: (rect.top - margin) / Math.max(1, view.height - rect.height - margin * 2)
          }));
        } catch (_) { /* Moving still works when browser storage is unavailable. */ }
      };
      const reset = () => {
        ['left', 'top', 'bottom', '--matty-panel-left', '--matty-panel-height'].forEach(name => this.style.removeProperty(name));
        this.removeAttribute('data-matty-moved');
        this.removeAttribute('data-matty-below');
        try { localStorage.removeItem(storageKey); } catch (_) {}
        placeLabel();
      };
      placeLabel();
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey));
        if (saved && [saved.x, saved.y].every(value => Number.isFinite(value) && value >= 0 && value <= 1)) {
          const rect = this.getBoundingClientRect();
          const view = viewport();
          move(margin + saved.x * Math.max(0, view.width - rect.width - margin * 2), margin + saved.y * Math.max(0, view.height - rect.height - margin * 2));
        }
      } catch (_) {}

      let pointer = null;
      let suppressClickUntil = 0;
      trigger.addEventListener('dragstart', event => event.preventDefault(), options);
      trigger.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        pressing = true;
        stopMotion();
        const rect = this.getBoundingClientRect();
        pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, moved: false };
      }, options);
      trigger.addEventListener('pointermove', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        if (!pointer.moved && Math.hypot(dx, dy) < 8) return;
        if (!pointer.moved) trigger.setPointerCapture(event.pointerId);
        pointer.moved = true;
        close();
        this.setAttribute('data-matty-dragging', '');
        event.preventDefault();
        move(pointer.left + dx, pointer.top + dy);
      }, options);
      const finish = event => {
        // Ignore the child's implicit touch capture when capture moves to summary.
        if (event.type === 'lostpointercapture' && event.target !== trigger) return;
        if (!pointer || pointer.id !== event.pointerId) return;
        if (pointer.moved) {
          suppressClickUntil = performance.now() + 350;
          remember();
        } else if (event.type === 'pointerup' && event.pointerType === 'touch' && trigger.contains(event.target)) {
          // Handle taps once, including browsers that suppress a click after dragging.
          event.preventDefault();
          if (!details.open) bounce();
          details.open = !details.open;
          suppressClickUntil = performance.now() + 350;
        }
        pointer = null;
        pressing = false;
        if (!image.classList.contains('matty-bounce')) scheduleMotion();
        this.removeAttribute('data-matty-dragging');
      };
      trigger.addEventListener('pointerup', finish, options);
      trigger.addEventListener('pointercancel', finish, options);
      document.addEventListener('pointerup', finish, options);
      document.addEventListener('pointercancel', finish, options);
      trigger.addEventListener('lostpointercapture', finish, options);
      trigger.addEventListener('click', event => {
        if (performance.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        } else if (!details.open) bounce();
      }, { ...options, capture: true });
      trigger.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'Home') {
          event.preventDefault();
          close();
          reset();
          return;
        }
        const direction = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
        if (!direction) return;
        event.preventDefault();
        close();
        const rect = this.getBoundingClientRect();
        const step = event.shiftKey ? 32 : 16;
        move(rect.left + direction[0] * step, rect.top + direction[1] * step);
        remember();
      }, options);
      details.addEventListener('toggle', placePanel, options);
      window.addEventListener('resize', () => {
        placeLabel();
        if (!this.hasAttribute('data-matty-moved')) return;
        const rect = this.getBoundingClientRect();
        move(rect.left, rect.top);
      }, options);
    }
    disconnectedCallback() { this.cleanupMotion?.(); this.controller?.abort(); }
  });
}

